import React, { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Checklist, Panel, Icon, PlainButton, classnames } from "@culturehq/components";
import * as CHQ from "chq";

import { useDoor } from "app/hooks";
import { EventLink } from "app/links";
import { Trans } from "app/utils";

import FilterAccordian from "components/common/FilterAccordian";
import DuplicateEvent from "components/event/modals/DuplicateEvent";
import MessageGuests from "components/event/modals/MessageGuests";
import CancelEvent from "components/event/modals/CancelEvent";
import CheckInModal from "components/event/modals/CheckInModal";
import eventForms from "components/events/eventForms";

import styles from "./EventSidebar.module.scss";

interface EnhancedEvent extends CHQ.Event {
  isUserHostOrAdmin: (_profile: CHQ.Profile) => boolean;
  onEventUpdate: () => void;
}

export const CreateEventSidebar = () => {
  const { open: openOnMobile, onToggle } = useDoor(false);

  return (
    <div className="event-sidebar">
      <div className="mobile_btn">
        <PlainButton onClick={onToggle} className="button toggle-filters">
          <Icon icon="gear-b" />
          <Trans>Create Event Details</Trans>
        </PlainButton>
        <Panel className={openOnMobile ? "open" : "closed"}>
          <EventCreateMenu />
        </Panel>
      </div>
      <Panel className={classnames(styles["sidebar-fixed"], "sidebar-fixed")}>
        <div className={classnames("col-md-12", "p-0")}>
          <h1 className={styles["form-title"]}><Trans>CREATE EVENT</Trans></h1>
          <FilterAccordian title={<CustomFilterTitle />} initialOpen className="p-0 base-menu">
            <EventCreateMenu />
          </FilterAccordian>
        </div>
      </Panel>
    </div>
  );
};

type UpdateEventProps = {
  event: EnhancedEvent;
  profile: CHQ.Profile;
  onEventUpdate: Function;
};

export const UpdateEventSideBar: React.FC<UpdateEventProps> = ({
  event, profile, onEventUpdate
}) => {
  const isHost = event.isUserHostOrAdmin(profile);
  const { open: openOnMobile, onToggle } = useDoor(false);
  const [toggleMenu, setToggleMenu] = useState(0);

  const handleManageEvent = () => {
    onToggle();
    setToggleMenu(1);
  };

  const handleQuickAction = () => {
    onToggle();
    setToggleMenu(2);
  };

  return (
    <div className="event-sidebar">
      <div className="mobile_btn">
        <div>
          <div className="col-xs-6 p-0">
            <PlainButton onClick={handleManageEvent} className="button toggle-filters m-btn">
              <Icon icon="gear-b" />
              <Trans>Manage Event </Trans>
            </PlainButton>
          </div>
          { event.cancelledAt === null && (
            <div className="col-xs-6 p-r-0">
              <PlainButton onClick={handleQuickAction} className="button toggle-filters m-btn drop-btn">
                <Trans>Quick Actions </Trans>
              </PlainButton>
            </div>
          )}
        </div>
        <MobilePanelSideBar
          event={event}
          openOnMobile={openOnMobile}
          isHost={isHost}
          profile={profile}
          toggleMenu={toggleMenu}
          onEventUpdate={onEventUpdate}
        />
      </div>
      <Panel className={classnames(styles["sidebar-fixed"], "sidebar-fixed")}>
        <div className={classnames("col-md-12", "p-0")}>
          <EventDetail
            event={event}
            isHost={isHost}
            profile={profile}
            onEventUpdate={onEventUpdate}
          />
        </div>
      </Panel>
    </div>
  );
};

type MobileSideProps = {
  event: EnhancedEvent;
  openOnMobile: boolean;
  isHost: boolean;
  profile: CHQ.Profile;
  toggleMenu: number;
  onEventUpdate: Function;
}

export const MobilePanelSideBar: React.FC<MobileSideProps> = ({
  event, openOnMobile, isHost, profile, toggleMenu, onEventUpdate
}) => (
  <div className={classnames(styles["view-sec"], "view-sec")}>
    <Panel className={openOnMobile ? "open" : "closed"}>
      { (toggleMenu === 1)
        ? (
          <ManageEvent event={event} profile={profile} />
        )
        : (
          <QuickActions event={event} isHost={isHost} onEventUpdate={onEventUpdate} />
        ) }
    </Panel>
  </div>
);

type QuickActionsProps = {
  event: EnhancedEvent;
  isHost: boolean;
  onEventUpdate: Function;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ event, isHost, onEventUpdate }) => (
  <ul className="e_label">
    {isHost && (
      <li>
        <MessageGuests event={event} />
      </li>
    )}
    <li>
      <DuplicateEvent event={event} />
    </li>
    {isHost && (
      <>
        <li>
          <CancelEvent event={event} onEventUpdate={onEventUpdate} />
        </li>
        <li>
          <CheckInModal event={event} />
        </li>
      </>
    )}
  </ul>
);

type ManageEventProps = {
  event: EnhancedEvent;
  profile: CHQ.Profile;
}

export const ManageEvent: React.FC<ManageEventProps> = ({ event, profile }) => {
  const eventFormsArr = ["type", "basic", "visibility", "advanced"];
  const indexEventForm = eventFormsArr.findIndex(element => window.location.href.includes(element));
  const initialOpen = indexEventForm >= 0;
  return (
    <>
      { event.isUserHostOrAdmin(profile) && (
        <>
          <FilterAccordian title={<CustomFilterTitle />} initialOpen={initialOpen} className={classnames(styles["m-t-15"], "p-0")}>
            <ul className="f-label">
              <li>
                <NavLink to={`/events/${event.id}/edit/type`} exact activeClassName="active">
                  <Trans>Event Type</Trans>
                </NavLink>
              </li>
              <li>
                <NavLink to={`/events/${event.id}/edit/basic`} exact activeClassName="active">
                  <Trans>Basic Info</Trans>
                </NavLink>
              </li>
              <li>
                <NavLink to={`/events/${event.id}/edit/visibility`} exact activeClassName="active">
                  <Trans>Visibility</Trans>
                </NavLink>
              </li>
              <li>
                <NavLink to={`/events/${event.id}/edit/advanced`} exact activeClassName="active">
                  <Trans>Advanced Settings</Trans>
                </NavLink>
              </li>
            </ul>
          </FilterAccordian>
        </>
      ) }
      <ul className="e_label">
        <li>
          <NavLink to={`/events/${event.id}/survey`} exact activeClassName="active">
            <Icon className="ac_icon" icon="analytics-ol" /> <Trans>Survey</Trans>
          </NavLink>
        </li>
        <li>
          <NavLink to={`/events/${event.id}/expenses`} exact activeClassName="active">
            <Icon className="ac_icon" icon="coin-ol" /> <Trans>Expenses</Trans>
          </NavLink>
        </li>
      </ul>
    </>
  );
};

const EventCreateMenu: React.FC = () => {
  const { eventForm: currentForm } = useParams();
  const currentIndex = eventForms.findIndex(element => element.key === currentForm) || 0;

  return (
    <Checklist className={styles.eventMenu}>
      {eventForms.map((eventForm, index) => (
        <Checklist.Item
          label={eventForm.name}
          key={eventForm.key}
          checked={index < currentIndex}
          className={styles.eventMenuItem}
          aria-current={eventForm.key === currentForm}
        >
          {eventForm.name}
        </Checklist.Item>
      ))}
    </Checklist>
  );
};

type EventDetailProps = {
  event: EnhancedEvent;
  isHost: boolean;
  profile: CHQ.Profile;
  onEventUpdate: Function;
}

const EventDetail: React.FC<EventDetailProps> = ({ event, profile, isHost, onEventUpdate }) => (
  <div className={classnames(styles["view-sec"], "view-sec")}>
    <h4 className={styles.displayTitle}>
      <EventLink event={event}>
        {event.name}
      </EventLink>
    </h4>
    <hr />
    { event.isUserHostOrAdmin(profile) && (
      <>
        <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>MANAGE EVENT</Trans></h4>
        <ManageEvent event={event} profile={profile} />
      </>
    )}
    <hr />
    { event.cancelledAt === null && (
      <>
        <h4 className={classnames(styles["form-title"], styles["p-t-0"])}><Trans>QUICK ACTIONS</Trans></h4>
        <QuickActions event={event} isHost={isHost} onEventUpdate={onEventUpdate} />
      </>
    )}
  </div>
);

const CustomFilterTitle = () => (
  <>
    <Icon
      className={classnames(styles["filter-icon"], "filter-icon")}
      icon="gear-b"
    />
    <span className={styles.fTitle}><Trans>Details</Trans></span>
  </>
);
